import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  getKYCProfile, 
  getRequiredKYCDocuments, 
  uploadKYCDocument, 
  verifySIRET,
  getComplianceStatus 
} from '../services/api';
import { toast } from 'sonner';
import { 
  Shield, 
  Upload, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Building2,
  Loader2,
  BadgeCheck,
  FileCheck,
  CreditCard,
  Truck as TruckIcon
} from 'lucide-react';

const KYCPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [verifyingSiret, setVerifyingSiret] = useState(false);
  const [kycProfile, setKycProfile] = useState(null);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [siretInput, setSiretInput] = useState('');
  const fileInputRefs = useRef({});

  useEffect(() => {
    loadKYCData();
  }, []);

  const loadKYCData = async () => {
    try {
      setLoading(true);
      const [profileData, docsData, complianceData] = await Promise.all([
        getKYCProfile(),
        getRequiredKYCDocuments(),
        getComplianceStatus()
      ]);
      setKycProfile(profileData);
      setRequiredDocs(docsData.required_documents || []);
      setCompliance(complianceData);
    } catch (error) {
      console.error('Error loading KYC data:', error);
      toast.error('Erreur lors du chargement des données KYC');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (docType, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    
    if (file.size > maxSize) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté (PDF, JPG, PNG uniquement)');
      return;
    }

    try {
      setUploading(docType);
      
      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      await uploadKYCDocument(docType, base64, file.name);
      toast.success('Document téléchargé avec succès');
      await loadKYCData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploading(null);
    }
  };

  const handleSiretVerify = async () => {
    if (!siretInput.trim()) {
      toast.error('Veuillez entrer un numéro SIRET');
      return;
    }

    try {
      setVerifyingSiret(true);
      const result = await verifySIRET(siretInput);
      
      if (result.valid) {
        toast.success('SIRET vérifié avec succès');
        await loadKYCData();
      } else {
        toast.error(result.error || 'SIRET invalide');
      }
    } catch (error) {
      console.error('SIRET verification error:', error);
      toast.error('Erreur lors de la vérification');
    } finally {
      setVerifyingSiret(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'expired':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default:
        return <FileText className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      verified: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      expired: 'bg-orange-100 text-orange-700 border-orange-200',
      not_uploaded: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return variants[status] || variants.not_uploaded;
  };

  const getDocIcon = (docType) => {
    switch (docType) {
      case 'identity_card':
      case 'passport':
        return CreditCard;
      case 'kbis':
        return Building2;
      case 'insurance_rc_pro':
        return Shield;
      case 'transport_license':
        return TruckIcon;
      default:
        return FileText;
    }
  };

  const getBadgeLevelColor = (level) => {
    switch (level) {
      case 'premium':
        return 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white';
      case 'verified':
        return 'bg-emerald-500 text-white';
      case 'basic':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-slate-200 text-slate-600';
    }
  };

  // Calculate progress
  const calculateProgress = () => {
    if (!requiredDocs.length) return 0;
    const verified = requiredDocs.filter(d => d.status === 'verified').length;
    const siretDone = compliance?.siret_verified ? 1 : 0;
    const total = requiredDocs.length + 1; // +1 for SIRET
    return Math.round(((verified + siretDone) / total) * 100);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </Layout>
    );
  }

  const progress = calculateProgress();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
              {t('kyc.title')}
            </h1>
            <p className="text-slate-600 mt-1">{t('kyc.subtitle')}</p>
          </div>
          
          {compliance && (
            <Badge className={`${getBadgeLevelColor(compliance.badge_level)} px-4 py-2 text-sm`}>
              <BadgeCheck className="w-4 h-4 mr-2" />
              {t(`kyc.compliance.badge.${compliance.badge_level}`)}
            </Badge>
          )}
        </div>

        {/* Progress Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Progression KYC</span>
              <span className="text-sm font-bold text-slate-900">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
            <div className="flex items-center gap-4 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span>{t('kyc.status.verified')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span>{t('kyc.status.pending')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-slate-300"></div>
                <span>Non soumis</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Status */}
        {compliance && (
          <Card className={compliance.overall_compliant ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                {compliance.overall_compliant ? (
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 shrink-0" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-amber-500 shrink-0" />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">
                    {compliance.overall_compliant ? 'Profil conforme' : 'Vérification en cours'}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {compliance.overall_compliant 
                      ? 'Votre dossier est complet. Vous avez accès à toutes les fonctionnalités.'
                      : 'Certaines vérifications sont en attente. Complétez votre dossier pour débloquer toutes les fonctionnalités.'
                    }
                  </p>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                      compliance.can_create_missions ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {compliance.can_create_missions ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      {t('kyc.compliance.canCreateMissions')}
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                      compliance.can_bid ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {compliance.can_bid ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      {t('kyc.compliance.canBid')}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* SIRET Verification */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                {t('kyc.siret.title')}
              </CardTitle>
              <CardDescription>{t('kyc.siret.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {compliance?.siret_verified ? (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  <div>
                    <p className="font-medium text-emerald-700">{t('kyc.siret.verified')}</p>
                    <p className="text-sm text-slate-600">SIRET: {kycProfile?.siret_number}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('kyc.siret.placeholder')}
                      value={siretInput}
                      onChange={(e) => setSiretInput(e.target.value)}
                      className="font-mono"
                      data-testid="siret-input"
                    />
                    <Button 
                      onClick={handleSiretVerify}
                      disabled={verifyingSiret}
                      data-testid="siret-verify-btn"
                    >
                      {verifyingSiret ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('kyc.siret.verify')
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Format: 14 chiffres (ex: 12345678900012)
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-orange-500" />
                Documents requis
              </CardTitle>
              <CardDescription>
                {requiredDocs.filter(d => d.status === 'verified').length} / {requiredDocs.length} documents validés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {requiredDocs.map((doc) => (
                  <div 
                    key={doc.type}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      {getStatusIcon(doc.status)}
                      <span className="text-sm">{t(`kyc.documents.${doc.type}`)}</span>
                    </div>
                    <Badge variant="outline" className={getStatusBadge(doc.status)}>
                      {t(`kyc.status.${doc.status === 'not_uploaded' ? 'not_started' : doc.status}`)}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Document Upload Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {requiredDocs.map((doc) => {
            const DocIcon = getDocIcon(doc.type);
            const existingDoc = kycProfile?.documents?.find(d => d.document_type === doc.type);
            
            return (
              <Card key={doc.type} className="relative overflow-hidden">
                {doc.status === 'verified' && (
                  <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                    <div className="absolute transform rotate-45 bg-emerald-500 text-white text-xs font-bold py-1 right-[-35px] top-[8px] w-[120px] text-center">
                      Vérifié
                    </div>
                  </div>
                )}
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-3 text-base">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      doc.status === 'verified' ? 'bg-emerald-100' : 'bg-slate-100'
                    }`}>
                      <DocIcon className={`w-5 h-5 ${
                        doc.status === 'verified' ? 'text-emerald-600' : 'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <span className="block">{t(`kyc.documents.${doc.type}`)}</span>
                      <Badge variant="outline" className={`${getStatusBadge(doc.status)} text-xs mt-1`}>
                        {t(`kyc.status.${doc.status === 'not_uploaded' ? 'not_started' : doc.status}`)}
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {existingDoc && (
                    <div className="text-sm text-slate-600 space-y-1">
                      <p className="truncate">📄 {existingDoc.document_name}</p>
                      {existingDoc.verified_at && (
                        <p className="text-emerald-600">{t('kyc.verifiedAt')}: {new Date(existingDoc.verified_at).toLocaleDateString()}</p>
                      )}
                      {existingDoc.rejection_reason && (
                        <p className="text-red-600 text-xs">{t('kyc.rejectionReason')}: {existingDoc.rejection_reason}</p>
                      )}
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={el => fileInputRefs.current[doc.type] = el}
                    onChange={(e) => handleFileSelect(doc.type, e)}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    data-testid={`file-input-${doc.type}`}
                  />
                  
                  <Button
                    variant={doc.status === 'verified' ? 'outline' : 'default'}
                    className="w-full"
                    disabled={uploading === doc.type}
                    onClick={() => fileInputRefs.current[doc.type]?.click()}
                    data-testid={`upload-btn-${doc.type}`}
                  >
                    {uploading === doc.type ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Téléchargement...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {existingDoc ? t('kyc.uploadNew') : t('kyc.upload')}
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-slate-500 text-center">
                    {t('kyc.maxSize')}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Help Card */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-slate-400 shrink-0" />
              <div>
                <h4 className="font-semibold text-slate-900">Pourquoi la vérification KYC ?</h4>
                <p className="text-sm text-slate-600 mt-1">
                  La vérification de votre identité et de vos documents est obligatoire pour garantir la sécurité 
                  des transactions sur Flitflow. Vos données sont protégées conformément au RGPD.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default KYCPage;
